/*
 * What colour a paper note is.
 *
 * Six tints, cycled by the note's position on the board. Nothing is stored:
 * a tint is not a property of a pain point, it is a property of where the pain
 * point sits, so there is no colour field on the document and no colour in any
 * write. Every client derives the same answer from the same list.
 *
 * POSITION MEANS CREATION ORDER, not row order. Those are the same thing on the
 * board for most of the round, but they part company twice, and both times the
 * row is the wrong number:
 *
 *   - The tally re-sorts the board by votes. A note that changed colour as it
 *     moved would be unrecognisable at the moment the room is matching the
 *     winner against what it just read.
 *   - The composer sits ABOVE your own notes, so the note you just filled in
 *     is row 1 the instant it lands. Keyed to the row, finishing a note
 *     repainted it — and paper does not change colour when you finish writing
 *     on it. Keyed to creation order, the first note keeps the first tint and
 *     the fresh blank sheet above it takes the second.
 *
 * A note being COMPOSED passes `pains.length` — the position it is about to be
 * created at — so the blank sheet you type on is already the colour it keeps.
 *
 * The one thing that does repaint is deleting a note: every note created after
 * it moves up a position and takes the tint below. That is inherent to colour
 * by position, and it only happens on your own stack during the writing phase,
 * where nobody else is looking at the board.
 */

/**
 * The tints, in the design's order. Mirrors `--note-*` in `theme/theme.css` —
 * edit the two together.
 */
export const NOTE_TINTS = [
  "var(--note-cream)",
  "var(--note-green)",
  "var(--note-blue)",
  "var(--note-lavender)",
  "var(--note-peach)",
  "var(--note-pink)",
] as const

/*
 * Tilt is picked the same way, so a board looks scattered without anyone
 * deciding it. Four of them against six tints: the two cycles only realign
 * every twelve notes, which is longer than any board gets.
 */
const NOTE_TILTS = [-1.4, 1.1, -0.7, 1.6] as const

/** Wraps a position into a cycle, negatives included. */
function wrap(position: number, length: number): number {
  return ((Math.trunc(position) % length) + length) % length
}

export function noteTint(position: number): string {
  return NOTE_TINTS[wrap(position, NOTE_TINTS.length)]
}

export function noteTilt(position: number): number {
  return NOTE_TILTS[wrap(position, NOTE_TILTS.length)]
}
